
import React, { useState, useEffect, useContext, useLayoutEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import AddButton from "../../Components/CommonComponent/Button/AddButton";
import Pagination, { usePagination } from "../../Components/CommonComponent/Pgination/pagination";
import AddInventory from "./AddInventory";
import LoadingSpinner from "../../Components/CommonComponent/loadingSpiner/LoadingSpinner";
import { FaEye } from "react-icons/fa";
import ViewInventoryModal from "./ViewInventoryDetails";
import { CommonContext } from "../../App";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import ContactMarquee from "../../Components/CommonComponent/Table/ContactMarquee";
import api from "../../services/api";

const buildGroupSummary = (data) => {
  const groups = {};

  data.forEach((item) => {
    const groupId = item.group_id || "no-group";

    if (!groups[groupId]) {
      groups[groupId] = {
        groupId,
        groupName: item.groupName || "Ungrouped",
        groupType: item.groupType || "",
        groupNameInHindi: item.groupNameInHindi || "",
        opening: 0,
        inQty: 0,
        inAmount: 0,
        outQty: 0,
        outAmount: 0,
        closing: 0,
      };
    }

    groups[groupId].opening += Number(item.opening_stock || 0);
    groups[groupId].inQty += Number(item.in_side || 0);
    groups[groupId].inAmount += Number(item.total_in_price || 0);
    groups[groupId].outQty += Number(item.out_side || 0);
    groups[groupId].outAmount += Number(item.total_out_price || 0);
  });

  Object.values(groups).forEach((g) => {
    g.closing = g.opening + g.inQty - g.outQty;
  });

  return Object.values(groups);
};

export default function InventoryCategoryTable() {
  const { t } = useTranslation();
  const { branchId, dateRange } = useContext(CommonContext);
  const navigate = useNavigate();
  const [inventory, setInventory] = useState({
    allData: [],
    pagination: { totalRecords: 0 },
  });

  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [selectedRows, setSelectedRows] = useState(new Set());

  const containerRef = React.useRef(null);
  const rowRefs = React.useRef([]);
  const { currentPage, itemsPerPage, handlePageChange, handleItemsPerPageChange, getPaginatedData } =
    usePagination(10);
  const paginationData = getPaginatedData(inventory);

  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback((e) => {
    const data = paginationData.currentItems;
    if (!data?.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev === -1 ? 0 : Math.min(prev + 1, data.length - 1)
      );
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev === -1 ? data.length - 1 : Math.max(prev - 1, 0)
      );
    }
    if (e.key === " ") {
      e.preventDefault();
      if (focusedIndex >= 0) {
        const id = data[focusedIndex].groupId;
        setSelectedRows((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(id)) newSet.delete(id);
          else newSet.add(id);
          return newSet;
        });
      }
    }
    if (e.key === "Enter") {
      if (focusedIndex >= 0) {
        handleRowClick(data[focusedIndex]);
      }
    }
  }, [paginationData.currentItems, focusedIndex]);

  useEffect(() => {
    const handleGlobalKey = (e) => {
      if (["ArrowDown", "ArrowUp"].includes(e.key)) {
        if (document.activeElement !== containerRef.current) {
          containerRef.current?.focus();
          handleKeyDown(e);
        }
      }
    };
    window.addEventListener("keydown", handleGlobalKey);
    return () => window.removeEventListener("keydown", handleGlobalKey);
  }, [handleKeyDown]);

  useLayoutEffect(() => {
    if (focusedIndex < 0) return;

    const row = rowRefs.current[focusedIndex];
    const container = containerRef.current;
    if (!row || !container) return;

    const containerHeight = container.clientHeight;
    const scrollTop = container.scrollTop;
    const rowTop = row.offsetTop;
    const rowHeight = row.offsetHeight;
    const rowCenter = rowTop + rowHeight / 2;
    const containerCenter = scrollTop + containerHeight / 2;
    const OFFSET = 60;

    if (rowCenter > containerCenter + OFFSET) {
      container.scrollTo({ top: rowTop - containerHeight / 2 + rowHeight, behavior: "smooth" });
    } else if (rowCenter < containerCenter - OFFSET) {
      container.scrollTo({ top: rowTop - containerHeight / 2, behavior: "smooth" });
    }
  }, [focusedIndex]);

  useEffect(() => {
    setFocusedIndex(-1);
    setSelectedRows(new Set());
  }, [currentPage, itemsPerPage]);

  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setStartDate(dateRange.startDate);
      setEndDate(dateRange.endDate);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await api.get(
        `inventory/get-all-item?branchId=${branchId}&startDate=${startDate}&endDate=${endDate}`
      );

      if (res.data.success) {
        const rawData = Array.isArray(res.data.data) ? res.data.data : [];
        const groupSummary = buildGroupSummary(rawData);

        setInventory({
          allData: groupSummary,
          pagination: { totalRecords: groupSummary.length },
        });
      }
    } catch (err) {
      toast.error("Error fetching inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchId || !startDate || !endDate) return;
    fetchInventory();
  }, [branchId, startDate, endDate]);

  const handleViewGroup = (group) => {
    setViewData(group);
    setViewModalOpen(true);
  };

  // ✅ CHANGED: Now navigates to StockTypeSubGroupTable instead of directly to InventoryTable
  const handleRowClick = (row) => {
    navigate("/inventory-books/sub-category-table", { state: { category: row } });
  };

  return (
    <div className="flex justify-center">
      <div className="w-full flex flex-col">

        {/* HEADER */}
        <div className="flex justify-between items-center mt-2 py-[2px] bg-theme-headerBg px-2 text-theme-color">
          <div className="font-medium">{t("listOfcategories")}</div>
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="flex justify-center my-4">
            <LoadingSpinner size={70} color="#000000" />
          </div>
        ) : inventory.allData.length === 0 ? (
          <p className="text-center text-gray-500 italic text-sm py-4">
            {t("noDataAvailable")}
          </p>
        ) : (
          <div
            ref={containerRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            className="overflow-x-auto outline-none h-[70vh]"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-theme-tableBg text-theme-headerBg border-theme-headerBg">
                  <th className="border-b px-1 text-center font-medium">{t("srNumber")}</th>
                  <th className="border-b px-1 text-start font-medium">{t("categoryName")}</th>
                  <th className="border-b py-[2px] px-1 text-center font-medium">{t("openingStock")}</th>
                  <th className="border-b px-1 text-center font-medium">{t("quantityIn")}</th>
                  <th className="border-b px-1 text-center font-medium">{t("amountIn")}</th>
                  <th className="border-b px-1 text-center font-medium">{t("quantityOut")}</th>
                  <th className="border-b px-1 text-center font-medium">{t("amountOut")}</th>
                  <th className="border-b px-1 text-center font-medium">{t("closingStock")}</th>
                </tr>
              </thead>

              <tbody>
                {paginationData.currentItems.map((row, i) => {
                  const isFocused = i === focusedIndex;
                  const isSelected = selectedRows.has(row.groupId);

                  return (
                    <tr
                      key={row.groupId}
                      ref={(el) => (rowRefs.current[i] = el)}
                      onClick={() => {
                        setFocusedIndex(i);
                        handleRowClick(row);
                      }}
                      className={`
                        cursor-pointer
                        ${
                          isFocused
                            ? "bg-[#5c5b5b] text-white"
                            : isSelected
                            ? "bg-[#f4c48a]"
                            : "bg-theme-tableBg"
                        }
                      `}
                    >
                      <td className="border-b px-1 text-center">
                        {(currentPage - 1) * itemsPerPage + i + 1}
                      </td>
                      <td className="border-b px-1 text-start">
                        <ContactMarquee firstName={row.groupName} secondName={row.groupNameInHindi} />
                      </td>
                      <td className="border-b px-1 text-center">{row.opening}</td>
                      <td className="border-b px-1 text-center">{row.inQty}</td>
                      <td className="border-b py-[2px] px-1 text-center">{row.inAmount}</td>
                      <td className="border-b px-1 text-center">{row.outQty}</td>
                      <td className="border-b px-1 text-center">{row.outAmount}</td>
                      <td className="border-b px-1 text-center">{row.closing}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* PAGINATION */}
            <Pagination
              data={inventory}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              itemsPerPageOptions={[10, 20, 50]}
              showPageInfo
            />
          </div>
        )}
      </div>

      {/* OPTIONAL MODALS */}
      {isModalOpen && (
        <AddInventory handleCancel={() => setIsModalOpen(false)} />
      )}

      {viewModalOpen && (
        <ViewInventoryModal
          data={viewData}
          onClose={() => setViewModalOpen(false)}
        />
      )}
    </div>
  );
}